import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '@app/Hooks/useDebounce';
import api from '@service/DataService';
import { X } from 'lucide-react';
import { createTags } from '../redux/selectors';
import { update_tag } from '../redux/reducers';

const toMealOption = (meal) => {
  if (!meal) return null;
  if (typeof meal === 'string') {
    return { value: meal, label: meal };
  }

  const id = meal?._id || meal?.id || meal?.value;
  if (!id) return null;

  return {
    value: id,
    label: meal?.name || meal?.label || id
  };
};

const AttachMeal = ({ tag, onClose }) => {
  const dispatch = useDispatch();
  const loading = useSelector(createTags);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [mealOptions, setMealOptions] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [loadingTaggedMeals, setLoadingTaggedMeals] = useState(false);
  const [loadingMealOptions, setLoadingMealOptions] = useState(false);

  useEffect(() => {
    if (!tag?._id) return;

    let active = true;

    const fetchTaggedMeals = async () => {
      try {
        setLoadingTaggedMeals(true);
        const request = await api.get('/meals', {
          params: {
            page: 1,
            tags: tag._id
          }
        });

        const response = request?.data?.data;
        const meals = Array.isArray(response?.meals)
          ? response.meals
          : Array.isArray(response)
          ? response
          : [];

        if (!active) return;
        setSelectedMeals(meals.map(toMealOption).filter(Boolean));
      } catch (error) {
        if (!active) return;
        setSelectedMeals([]);
      } finally {
        if (!active) return;
        setLoadingTaggedMeals(false);
      }
    };

    fetchTaggedMeals();

    return () => {
      active = false;
    };
  }, [tag?._id]);

  useEffect(() => {
    if (!tag?._id) return;

    let active = true;

    const fetchMeals = async () => {
      try {
        setLoadingMealOptions(true);
        const request = await api.get('/meals', {
          params: {
            page: 1,
            limit: 20,
            search: debouncedSearch || undefined
          }
        });

        const response = request?.data?.data;
        const meals = Array.isArray(response?.meals)
          ? response.meals
          : Array.isArray(response)
          ? response
          : [];

        const options = meals.map(toMealOption).filter((meal) => meal.value);

        if (!active) return;

        setMealOptions(options);
      } catch (error) {
        if (!active) return;
        setMealOptions([]);
      } finally {
        if (!active) return;
        setLoadingMealOptions(false);
      }
    };

    fetchMeals();

    return () => {
      active = false;
    };
  }, [debouncedSearch, tag?._id]);

  const mergedOptions = useMemo(() => {
    const merged = [...selectedMeals, ...mealOptions];
    return merged.filter(
      (option, index, self) =>
        self.findIndex((item) => item.value === option.value) === index
    );
  }, [mealOptions, selectedMeals]);

  const removeMeal = (mealId) => {
    setSelectedMeals((prev) => prev.filter((meal) => meal.value !== mealId));
  };

  const handleSubmit = () => {
    if (!tag?._id) {
      toast.error('No tag selected');
      return;
    }

    const mealIds = selectedMeals.map((meal) => meal.value);

    dispatch(
      update_tag({
        id: tag._id,
        data: { meal_ids: mealIds },
        onClose
      })
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-[#222222] text-[20px] font-[600]">Attach Meals</h3>
      <p className="text-sm text-[#6B7280]">
        Tag: <span className="font-medium text-[#222222]">{tag?.name || '-'}</span>
      </p>

      <div className="flex flex-col gap-2">
        <label className="text-[14px] font-[500] text-[#374151]">Search meal</label>
        <Select
          isMulti
          options={mergedOptions}
          value={selectedMeals}
          placeholder="Search and select meals"
          className="text-sm"
          isLoading={loadingTaggedMeals || loadingMealOptions}
          onInputChange={(value, { action }) => {
            if (action === 'input-change') {
              setSearch(value);
            }
          }}
          onChange={(value) => setSelectedMeals(value || [])}
          noOptionsMessage={() => 'No meals found'}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {loadingTaggedMeals && (
          <p className="text-xs text-[#6B7280]">Loading already tagged meals...</p>
        )}
        {selectedMeals.map((meal) => (
          <span
            key={meal.value}
            className="flex gap-2 items-center p-2 text-xs bg-gray-100 rounded-full"
          >
            {meal.label}
            <button
              type="button"
              className="text-red-500"
              onClick={() => removeMeal(meal.value)}
            >
              <X size={14} />
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          className="px-4 py-2 rounded-md border border-[#E5E7EB] text-[#374151]"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="px-4 py-2 rounded-md bg-[#549a9a] text-white disabled:opacity-50"
          onClick={handleSubmit}
          disabled={loading || !tag?._id}
        >
          {loading ? 'Saving...' : 'Attach meals'}
        </button>
      </div>
    </div>
  );
};

export default AttachMeal;
